import { useLayoutEffect, useRef, useState } from "react";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import type { Course } from "@/types/course";
import type { SortKey } from "@/utils/courseSort";
import CourseCard from "./CourseCard";

interface CourseListProps {
  courses: Course[];
  selectedIds: string[];
  maxSelect: number;
  sortKey: SortKey;
  onToggle: (id: string) => void;
}

const ESTIMATED_ROW_HEIGHT = 200;

export default function CourseList({
  courses,
  selectedIds,
  maxSelect,
  sortKey,
  onToggle,
}: CourseListProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState(3);
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const updateScrollMargin = () => {
      setScrollMargin(el.getBoundingClientRect().top + window.scrollY);
    };

    updateScrollMargin();

    const ro = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      setColumns(width >= 1024 ? 3 : width >= 640 ? 2 : 1);
      updateScrollMargin();
    });
    ro.observe(el);

    window.addEventListener("resize", updateScrollMargin);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", updateScrollMargin);
    };
  }, []);

  const rowCount = Math.ceil(courses.length / columns);

  const rowVirtualizer = useWindowVirtualizer({
    count: rowCount,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 6,
    scrollMargin,
    getItemKey: (index) => `${columns}-${index}`,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const atMax = selectedIds.length >= maxSelect;

  const topPadding =
    virtualRows.length > 0 ? virtualRows[0].start - scrollMargin : 0;
  const bottomPadding =
    virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() -
        virtualRows[virtualRows.length - 1].end
      : 0;

  return (
    <div ref={listRef}>
      {topPadding > 0 && <div style={{ height: topPadding }} />}
      {virtualRows.map((virtualRow) => {
        const start = virtualRow.index * columns;
        const rowItems = courses.slice(start, start + columns);
        return (
          <div
            key={virtualRow.key}
            data-index={virtualRow.index}
            ref={rowVirtualizer.measureElement}
            className="grid grid-cols-1 gap-3 pb-3 sm:grid-cols-2 lg:grid-cols-3"
          >
            {rowItems.map((course) => {
              const checked = selectedIds.includes(course.id);
              const disabled = !checked && atMax;
              return (
                <div key={course.id} className="flex">
                  <CourseCard
                    course={course}
                    checked={checked}
                    disabled={disabled}
                    sortKey={sortKey}
                    onToggle={onToggle}
                  />
                </div>
              );
            })}
          </div>
        );
      })}
      {bottomPadding > 0 && <div style={{ height: bottomPadding }} />}
    </div>
  );
}